// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/types.ts
// Types du pipeline audio unifié.
//
// Trois concepts :
//   - AudioInput   : une entrée audio connectée au bus de mixage
//   - AudioEffect  : un plugin de traitement (lowcut, compresseur…)
//   - AudioLevel   : mesure de niveau L/R pour les VU-mètres
// ============================================================================

// ─── Niveaux audio ────────────────────────────────────────────────────────────

export interface AudioLevel {
  left:  number;   // 0–100
  right: number;   // 0–100
}

export const SILENT_LEVEL: AudioLevel = { left: 0, right: 0 };

// ─── Types d'entrée audio ─────────────────────────────────────────────────────

export type AudioInputType =
  | 'microphone'     // getUserMedia audio
  | 'media-element'  // <video> ou <audio> element (jingle, HLS)
  | 'stream'         // MediaStream existant (remote, screen avec audio)
  | 'buffer';        // AudioBuffer (sons courts, SoundSlot)

// ─── Configuration d'une entrée ───────────────────────────────────────────────

export interface AudioInputConfig {
  /** Identifiant unique (= sourceId du SourceRegistry) */
  id: string;
  /** Type d'entrée */
  type: AudioInputType;
  /** Volume initial 0–1 */
  volume?: number;
  /** Mute initial */
  muted?: boolean;
  /** Volume stéréo L/R (0–1 chacun) */
  volumeLeft?: number;
  volumeRight?: number;
  /** Monitoring local (sortie vers les speakers) — défaut true */
  monitor?: boolean;
  /** Lecture en boucle (pour type 'buffer') */
  loop?: boolean;
  /** Point de départ de lecture en secondes, pour type 'buffer' — défaut 0 */
  startTime?: number;
  /** Point de fin de lecture en secondes, pour type 'buffer' — défaut fin du buffer */
  endTime?: number;
}

// ─── État d'une entrée connectée ──────────────────────────────────────────────

export interface AudioInput {
  id:           string;
  type:         AudioInputType;
  /** Nœud source dans le graphe WebAudio */
  sourceNode:   AudioNode | null;
  /** Chaîne d'effets (peut être vide) */
  effectsChain: AudioEffect[];
  /** Nœud de gain principal (volume + mute utilisateur) */
  gainNode:     GainNode;
  /** Nœuds de gain stéréo L/R (après split) */
  gainLeft:     GainNode;
  gainRight:    GainNode;
  /** Splitter pour séparer L/R */
  splitter:     ChannelSplitterNode;
  /** Merger pour recombiner L/R */
  merger:       ChannelMergerNode;
  /**
   * Gain de sortie — intercalé entre merger et busDest.
   * Permet de couper la sortie WHIP sans interrompre le signal
   * des analyseurs (VU-mètres toujours actifs).
   * Valeur normale : 1. Coupé : 0. Transition douce via setTargetAtTime.
   */
  outputGain:   GainNode;
  /** Analyseurs pour mesure de niveau (après gainNode, avant merger) */
  analyserL:    AnalyserNode;
  analyserR:    AnalyserNode;
  /** Données fréquentielles pour la mesure */
  dataL:        Uint8Array;
  dataR:        Uint8Array;
  /** Dernier niveau mesuré */
  level:        AudioLevel;
  /** Configuration courante */
  volume:       number;
  muted:        boolean;
  volumeLeft:   number;
  volumeRight:  number;
  monitor:      boolean;
  /** Pour les sources 'buffer' : référence au BufferSourceNode actif */
  bufferSource: AudioBufferSourceNode | null;
  /** true si on possède le stream (à stopper au disconnect) */
  ownedStream:  MediaStream | null;
}

// ─── Plugin d'effet audio ─────────────────────────────────────────────────────

/**
 * Interface pour un plugin d'effet audio.
 *
 * Chaque effet reçoit un AudioContext et crée ses nœuds internes.
 * Il expose un nœud d'entrée et un nœud de sortie pour s'insérer
 * dans la chaîne : source → [effet1] → [effet2] → gain → bus.
 */
export interface AudioEffect {
  /** Identifiant unique de l'effet (ex: 'lowcut', 'compressor') */
  id: string;
  /** Nom affiché dans l'UI */
  label: string;
  /** true si l'effet est actif (bypass sinon) */
  enabled: boolean;
  /** Nœud d'entrée du graphe de l'effet */
  inputNode: AudioNode;
  /** Nœud de sortie du graphe de l'effet */
  outputNode: AudioNode;
  /** Active ou désactive l'effet */
  setEnabled(enabled: boolean): void;
  /** Libère les ressources WebAudio */
  destroy(): void;
}

/**
 * Factory pour créer un effet audio.
 */
export type AudioEffectFactory = (ctx: AudioContext) => AudioEffect;

// ─── Événements du mixer ──────────────────────────────────────────────────────

export type AudioMixerEventType =
  | 'input-connected'
  | 'input-disconnected'
  | 'level-update'
  | 'error';

export interface AudioMixerEvent {
  type:    AudioMixerEventType;
  inputId: string;
  level?:  AudioLevel;
  error?:  string;
}

export type AudioMixerEventListener = (event: AudioMixerEvent) => void;